import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import useLoading from '../useLoading';
import useApiErrorHandler from '../useApiErrorHandler';
import UiVisibilityService from '../../services/ui/UiVisibilityService';
import {
  startLoading,
  setUiSettings,
  setError,
  updateVisibilityState,
  resetUiVisibilityState,
} from '../../store/slices/ui/uiVisibilitySlice';

/**
 * 🎨 Hook para manejar toda la lógica del panel de visibilidad UI:
 * - Cargar configuraciones desde backend.
 * - Actualizar el estado visible/oculto de cada componente UI.
 * - Sincronizar con Redux para reflejar los cambios globalmente.
 */
export const useUiVisibility = () => {
  const dispatch: AppDispatch = useDispatch();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { handle } = useApiErrorHandler();

  const { settings, loading, error } = useSelector(
    (state: RootState) => state.uiVisibility
  );

  // =====================================================
  // 🌐 CONSULTAS
  // =====================================================

  /** 🔹 Obtener todas las configuraciones de visibilidad */
  const fetchUiSettings = async () => {
    if (settings.length > 0) return;

    try {
      dispatch(startLoading());
      showLoadingIndicator();

      const response = await UiVisibilityService.getAll();
      const { statusCode, data } = response;

      if (statusCode === 200 && Array.isArray(data)) {
        dispatch(setUiSettings(data));
      }
    } catch (error) {
      handle(error);
      dispatch(setError('Error al obtener configuraciones de UI'));
    } finally {
      hideLoadingIndicator();
    }
  };

  // =====================================================
  // 🔄 ACTUALIZACIONES
  // =====================================================

  /** 🔹 Alternar visibilidad de un componente UI */
  const toggleUiVisibility = async (code: string, currentVisible: boolean) => {
    try {
      showLoadingIndicator();

      const response = await UiVisibilityService.updateVisibility({
        code,
        visible: !currentVisible,
      });

      const { statusCode, data } = response;

      if (statusCode === 200 && typeof data === 'boolean') {
        // ✅ Aplicar valor real retornado desde el backend
        dispatch(updateVisibilityState({ code, visible: data }));
      }
    } catch (error) {
      handle(error);
      dispatch(setError('Error al actualizar visibilidad de componente UI'));
    } finally {
      hideLoadingIndicator();
    }
  };

  // =====================================================
  // 🧹 RESET
  // =====================================================

  const reset = () => dispatch(resetUiVisibilityState());

  // =====================================================
  // 📦 RETORNO DEL HOOK
  // =====================================================
  return {
    settings,
    loading,
    error,
    fetchUiSettings,
    toggleUiVisibility,
    reset,
  };
};

export default useUiVisibility;
